// Entry point.
//
// Two applications share one bundle entry and one origin: the client portal
// at /, and the firm console at /practice. Which one boots is decided here,
// from the path, before React renders anything.
//
// The console is loaded with `lazy`, so Vite emits it as a separate chunk.
// That matters because almost everyone who ever loads this page is a client
// filling in a questionnaire on a phone, and they should not be downloading a
// console they can never sign in to. react-router is part of that chunk too,
// which is why the console brings its own Router rather than being handed one
// here — importing it at this level put 25KB of routing into the bundle every
// client downloads on a phone.

import { StrictMode, Suspense, lazy } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './App.js';

// Named export wrapped into a default, which is the shape `lazy` requires.
const ConsoleApp = lazy(() =>
  import('./console/ConsoleApp.js').then((m) => ({ default: m.ConsoleApp })),
);

const root = document.getElementById('root');
if (!root) throw new Error('No #root element — check index.html');

// Read once, at boot. Navigating between the portal and the console is a full
// page load by design: they are different applications with different
// sessions, and sharing React state across them would be a bug waiting to
// happen rather than a feature.
const isConsole = window.location.pathname.startsWith('/practice');

createRoot(root).render(
  // StrictMode double-invokes effects in development, which is exactly how
  // you find a hook that is not idempotent. Autosave had better survive it.
  <StrictMode>
    {isConsole ? (
      // The fallback is deliberately unstyled text: console.css lives inside
      // the lazy chunk, so it has not loaded yet at this point.
      <Suspense fallback={<p style={{ padding: '2rem', fontFamily: 'system-ui' }}>Loading…</p>}>
        <ConsoleApp />
      </Suspense>
    ) : (
      <App />
    )}
  </StrictMode>,
);
